import { ArrowLeftOutlined } from '@ant-design/icons';
import { Button, Card, message, Spin, theme } from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Permission,
  type CreateCatalogCategoryDto,
  type UpdateCatalogCategoryDto,
} from '@aid/shared';
import { useHasPermission } from '../../auth/useHasPermission';
import { EmptyState } from '../../components/EmptyState';
import { PageHeader } from '../../components/PageHeader';
import { useCategoriaAdmin, useUpdateCategoriaAdmin } from './api/useCatalogosAdmin';
import { CatalogCategoryForm } from './components/CatalogCategoryForm';

export function CatalogCategoryEditPage() {
  const { category: code } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const { data: category, isLoading, error } = useCategoriaAdmin(code);
  const updateCategory = useUpdateCategoriaAdmin();
  const hasPermission = useHasPermission(Permission.CATALOGOS_UPDATE);
  const { token } = theme.useToken();

  if (!hasPermission) {
    return <div>No tenés permiso para editar categorías.</div>;
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 56 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (error || !category || !code) {
    return (
      <EmptyState
        title="Categoría no encontrada"
        description="La categoría que intentás editar no existe o fue desactivada."
        action={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate('/catalogos')}>
            Volver a catálogos
          </Button>
        }
      />
    );
  }

  const handleSubmit = async (values: CreateCatalogCategoryDto | UpdateCatalogCategoryDto) => {
    if ('code' in values) {
      await submitUpdate({
        name: values.name,
        description: values.description,
        parentCategoryCode: values.parentCategoryCode,
        behavior: values.behavior,
        active: values.active,
      });
      return;
    }
    await submitUpdate(values);
  };

  const submitUpdate = async (values: UpdateCatalogCategoryDto) => {
    try {
      await updateCategory.mutateAsync({ code, dto: values });
      message.success('Categoría actualizada correctamente');
      navigate(`/catalogos/category/${encodeURIComponent(code)}`);
    } catch {
      message.error('No se pudo actualizar la categoría');
    }
  };

  return (
    <div>
      <Button
        type="text"
        icon={<ArrowLeftOutlined />}
        onClick={() => navigate(`/catalogos/category/${encodeURIComponent(code)}`)}
        style={{ paddingLeft: 0, height: 'auto', marginBottom: 8 }}
      >
        Volver a la categoría
      </Button>
      <PageHeader title="Editar categoría" subtitle={category.name} />
      <Card
        style={{
          borderRadius: 12,
          border: `1px solid ${token.colorBorderSecondary}`,
          boxShadow: token.boxShadow,
        }}
      >
        <CatalogCategoryForm
          mode="edit"
          initialValues={category}
          onSubmit={handleSubmit}
          loading={updateCategory.isPending}
        />
      </Card>
    </div>
  );
}
